/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
} from 'react-native';
import { View } from 'react-native';
import DyteText from './DyteText';
import { TouchableHighlight } from 'react-native';
import DytePollForm from './DytePollForm';
import DytePoll from './DytePoll';
import DyteClient from '@dytesdk/web-core';
import { UIConfig } from '../types/ui-config';
import { defaultConfig } from '../lib/default-ui-config';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { useDyteSelector } from '@dytesdk/react-native-core';

import { States } from '../types/props';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function DytePolls({
  meeting,
  config = defaultConfig,
  iconPack = defaultIconPack,
  size = 'sm',
  t,
}: {
  meeting: DyteClient;
  config?: UIConfig;
  iconPack?: IconPack;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  t?: DyteI18n;
}) {
  const flatListRef = useRef(null);
  const polls = useDyteSelector((m) => m.polls.items);
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const [createPoll, enableCreatePoll] = useState(false);
  const scrollToBottom = () => {
    if (flatListRef.current) {
      flatListRef.current.scrollToEnd({ animated: true });
    }
  };

  useEffect(() => {
    if (flatListRef.current) {
      flatListRef.current.scrollToEnd({ animated: true });
    }
  }, [createPoll]);

  const styles = StyleSheet.create({
    joinButton: {
      width: '95%',
      height: 50,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
      marginBottom: 10,
    },
    pollsWrapper: {
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      backgroundColor: colors.background[900],
    },
    scroll: {
      paddingTop: 20,
      marginLeft: 5,
    },
    text: {
      fontSize: 15,
      marginLeft: 20,
      marginBottom: -5,
    },
    createButton: {
      alignItems: 'center',
      justifyContent: 'center',
      marginVertical: 5,
    },
  });

  if (
    !meeting.self.permissions.polls.canCreate &&
    !meeting.self.permissions.polls.canView
  )
    return null;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.pollsWrapper}
    >
      <FlatList
        ref={flatListRef}
        data={[...polls].reverse()}
        style={styles.scroll}
        keyExtractor={(poll) => poll.id.toString()}
        renderItem={({ item: poll }) => (
          <View>
            <DyteText style={styles.text}>
              {t('polls.by')}{' '}
              {meeting.polls.items.map((p) =>
                p.id === poll.id ? p.createdBy : ''
              )}
            </DyteText>
            <DytePoll
              poll={poll}
              onDyteVotePoll={(index) => {
                meeting.polls.vote(poll.id, index);
              }}
              meeting={meeting}
            />
          </View>
        )}
        ListFooterComponent={
          createPoll && (
            <DytePollForm
              onDyteCreatePoll={(
                pollQuestion,
                pollOptions,
                anonymous,
                hide
              ) => {
                meeting.polls.create(
                  pollQuestion,
                  pollOptions.map((item) => item),
                  anonymous,
                  hide
                );
                enableCreatePoll(false);
              }}
            />
          )
        }
      />
      {meeting.self.permissions.polls.canCreate && (
        <View style={styles.createButton}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            style={{
              ...styles.joinButton,
              backgroundColor: !createPoll
                ? colors.brand[500]
                : colors.background[800],
            }}
            onPress={() => {
              if (!createPoll) scrollToBottom();
              enableCreatePoll(!createPoll);
            }}
          >
            {/* eslint-disable-next-line react-native/no-inline-styles */}
            <View style={{ alignContent: 'center' }}>
              <DyteText
                children={
                  <DyteText
                    style={{
                      color: !createPoll ? colors.textOnBrand : colors.danger,
                    }}
                  >
                    {!createPoll ? t('polls.create') : t('polls.cancel')}
                  </DyteText>
                }
              />
            </View>
          </TouchableHighlight>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}
